import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ROIProjectionSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedScenario, setSelectedScenario] = useState('medium');

  const translations = {
    en: {
      title: "ROI Projections & Savings Calculator",
      subtitle: "See how much you can save with our ERP solution",
      scenarios: {
        small: "Small Operation",
        medium: "Medium Operation", 
        large: "Large Operation"
      },
      metrics: {
        title: "12-Month ROI Analysis",
        laborCost: "Labor Cost Reduction",
        qualityImprovement: "Quality Improvement",
        efficiencyGains: "Efficiency Gains",
        inventoryOptimization: "Inventory Optimization",
        totalSavings: "Total Annual Savings",
        erpCost: "ERP Annual Cost",
        netSavings: "Net Annual Savings",
        roiPercentage: "ROI Percentage",
        paybackPeriod: "Payback Period"
      },
      details: {
        small: {
          employees: "10-25 employees",
          monthlyProduction: "100-500 tons",
          laborCost: "₹2,40,000",
          qualityImprovement: "₹1,80,000",
          efficiencyGains: "₹1,20,000",
          inventoryOptimization: "₹80,000",
          totalSavings: "₹6,20,000",
          erpCost: "₹1,44,000",
          netSavings: "₹4,76,000",
          roiPercentage: "330%",
          paybackPeriod: "3 months"
        },
        medium: {
          employees: "26-100 employees",
          monthlyProduction: "500-2000 tons",
          laborCost: "₹6,00,000",
          qualityImprovement: "₹4,50,000",
          efficiencyGains: "₹3,00,000",
          inventoryOptimization: "₹2,50,000",
          totalSavings: "₹16,00,000",
          erpCost: "₹3,00,000",
          netSavings: "₹13,00,000",
          roiPercentage: "433%",
          paybackPeriod: "2.5 months"
        },
        large: {
          employees: "100+ employees",
          monthlyProduction: "2000+ tons",
          laborCost: "₹15,00,000",
          qualityImprovement: "₹12,00,000",
          efficiencyGains: "₹8,00,000",
          inventoryOptimization: "₹6,00,000",
          totalSavings: "₹41,00,000",
          erpCost: "₹6,00,000",
          netSavings: "₹35,00,000",
          roiPercentage: "583%",
          paybackPeriod: "2 months"
        }
      }
    },
    hi: {
      title: "ROI प्रोजेक्शन और बचत कैलकुलेटर",
      subtitle: "देखें कि आप हमारे ERP समाधान से कितना बचा सकते हैं",
      scenarios: {
        small: "छोटा संचालन",
        medium: "मध्यम संचालन",
        large: "बड़ा संचालन"
      },
      metrics: {
        title: "12-माह ROI विश्लेषण",
        laborCost: "श्रम लागत में कमी",
        qualityImprovement: "गुणवत्ता में सुधार",
        efficiencyGains: "दक्षता लाभ",
        inventoryOptimization: "इन्वेंटरी अनुकूलन",
        totalSavings: "कुल वार्षिक बचत",
        erpCost: "ERP वार्षिक लागत",
        netSavings: "शुद्ध वार्षिक बचत",
        roiPercentage: "ROI प्रतिशत",
        paybackPeriod: "पेबैक अवधि"
      },
      details: {
        small: {
          employees: "10-25 कर्मचारी",
          monthlyProduction: "100-500 टन",
          laborCost: "₹2,40,000",
          qualityImprovement: "₹1,80,000",
          efficiencyGains: "₹1,20,000",
          inventoryOptimization: "₹80,000",
          totalSavings: "₹6,20,000",
          erpCost: "₹1,44,000",
          netSavings: "₹4,76,000",
          roiPercentage: "330%",
          paybackPeriod: "3 महीने"
        },
        medium: {
          employees: "26-100 कर्मचारी",
          monthlyProduction: "500-2000 टन",
          laborCost: "₹6,00,000",
          qualityImprovement: "₹4,50,000",
          efficiencyGains: "₹3,00,000",
          inventoryOptimization: "₹2,50,000",
          totalSavings: "₹16,00,000",
          erpCost: "₹3,00,000",
          netSavings: "₹13,00,000",
          roiPercentage: "433%",
          paybackPeriod: "2.5 महीने"
        },
        large: {
          employees: "100+ कर्मचारी",
          monthlyProduction: "2000+ टन",
          laborCost: "₹15,00,000",
          qualityImprovement: "₹12,00,000",
          efficiencyGains: "₹8,00,000",
          inventoryOptimization: "₹6,00,000",
          totalSavings: "₹41,00,000",
          erpCost: "₹6,00,000",
          netSavings: "₹35,00,000",
          roiPercentage: "583%",
          paybackPeriod: "2 महीने"
        }
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const currentData = t.details[selectedScenario];

  return (
    <section id="roi-projections" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Scenario Selector */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-surface-100 rounded-lg p-1">
            {Object.entries(t.scenarios).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSelectedScenario(key)}
                className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                  selectedScenario === key
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-text-secondary hover:text-primary'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* ROI Breakdown */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            <div className="mb-8">
              <h3 className="text-xl font-bold text-primary mb-4">
                {t.metrics.title}
              </h3>
              <div className="text-sm text-text-secondary mb-2">
                {currentData.employees} • {currentData.monthlyProduction}
              </div>
            </div>

            <div className="space-y-6">
              {/* Labor Cost Reduction */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Icon name="Users" size={20} color="var(--color-blue-600)" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{t.metrics.laborCost}</div>
                    <div className="text-sm text-text-secondary">
                      {currentLanguage === 'en' ? '40% reduction in manual work' : '40% मैन्युअल काम में कमी'}
                    </div>
                  </div>
                </div>
                <div className="text-xl font-bold text-blue-600">
                  {currentData.laborCost}
                </div>
              </div>

              {/* Quality Improvement */}
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} color="var(--color-green-600)" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{t.metrics.qualityImprovement}</div>
                    <div className="text-sm text-text-secondary">
                      {currentLanguage === 'en' ? 'Reduced defects & rework' : 'दोष और पुनर्कार्य में कमी'}
                    </div>
                  </div>
                </div>
                <div className="text-xl font-bold text-green-600">
                  {currentData.qualityImprovement}
                </div>
              </div>

              {/* Efficiency Gains */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Icon name="Zap" size={20} color="var(--color-purple-600)" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{t.metrics.efficiencyGains}</div>
                    <div className="text-sm text-text-secondary">
                      {currentLanguage === 'en' ? 'Faster processes & automation' : 'तेज़ प्रक्रियाएं और स्वचालन'}
                    </div>
                  </div>
                </div>
                <div className="text-xl font-bold text-purple-600">
                  {currentData.efficiencyGains}
                </div>
              </div>

              {/* Inventory Optimization */}
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Icon name="Package" size={20} color="var(--color-orange-600)" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{t.metrics.inventoryOptimization}</div>
                    <div className="text-sm text-text-secondary">
                      {currentLanguage === 'en' ? 'Reduced stock holding costs' : 'स्टॉक होल्डिंग लागत में कमी'}
                    </div>
                  </div>
                </div>
                <div className="text-xl font-bold text-orange-600">
                  {currentData.inventoryOptimization}
                </div>
              </div>
            </div>
          </div>

          {/* ROI Summary */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            <div className="text-center mb-8">
              <Icon name="TrendingUp" size={48} color="var(--color-primary)" className="mx-auto mb-4" />
              <h3 className="text-xl font-bold text-primary">
                {currentLanguage === 'en' ? 'ROI Summary' : 'ROI सारांश'}
              </h3>
            </div>

            <div className="space-y-6">
              {/* Total Savings */}
              <div className="bg-success-50 rounded-lg p-6">
                <div className="text-center">
                  <div className="text-sm text-text-secondary mb-2">
                    {t.metrics.totalSavings}
                  </div>
                  <div className="text-3xl font-bold text-success">
                    {currentData.totalSavings}
                  </div>
                </div>
              </div>

              {/* ERP Cost */}
              <div className="flex justify-between items-center py-4 border-b border-border">
                <span className="text-text-secondary">{t.metrics.erpCost}</span>
                <span className="font-medium text-red-600">-{currentData.erpCost}</span>
              </div>

              {/* Net Savings */}
              <div className="bg-primary-50 rounded-lg p-6">
                <div className="text-center">
                  <div className="text-sm text-text-secondary mb-2">
                    {t.metrics.netSavings}
                  </div>
                  <div className="text-3xl font-bold text-primary">
                    {currentData.netSavings}
                  </div>
                </div>
              </div>

              {/* ROI Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-accent-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-accent mb-1">
                    {currentData.roiPercentage}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {t.metrics.roiPercentage}
                  </div>
                </div>
                <div className="bg-secondary-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-secondary mb-1">
                    {currentData.paybackPeriod}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {t.metrics.paybackPeriod}
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-text-secondary text-center">
                  {currentLanguage === 'en' ?'* ROI calculations based on average client results and industry benchmarks. Actual results may vary.' :'* ROI गणना औसत क्लाइंट परिणामों और उद्योग बेंचमार्क पर आधारित है। वास्तविक परिणाम भिन्न हो सकते हैं।'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROIProjectionSection;